
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Index = () => {
  const { user, loading, canAccessSuperAdmin, canAccessBrandAdmin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [forceShowLogin, setForceShowLogin] = useState(false);

  // Timeout fallback to show login if loading takes too long
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('⚠️ Index: Loading timeout reached after 3 seconds, forcing login display');
        setForceShowLogin(true);
      }
    }, 3000); // 3 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Redirect authenticated users
  useEffect(() => {
    if (!loading && user) {
      console.log('🔄 Index: Authenticated user detected, redirecting...', {
        email: user.email,
        isSuperAdmin: canAccessSuperAdmin?.(),
        isBrandAdmin: canAccessBrandAdmin?.()
      });
      
      if (canAccessSuperAdmin?.() || canAccessBrandAdmin?.()) {
        console.log('🔄 Index: Redirecting to /admin');
        navigate('/admin', { replace: true });
      } else {
        console.log('🔄 Index: Redirecting to /dashboard');
        navigate('/dashboard', { replace: true });
      }
    } else if (!loading && !user) {
      console.log('📝 Index: No user found, showing login form');
    }
  }, [user, loading, canAccessSuperAdmin, canAccessBrandAdmin, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSigningIn(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error(error.message);
      } else {
        // User will be redirected by the useEffect above
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSigningIn(false);
    }
  };

  // Show loading only if actually loading and haven't timed out
  if (loading && !forceShowLogin) {
    console.log('⏳ Index: Showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If we have a user but we're forcing login display (shouldn't happen due to redirect)
  if (user && forceShowLogin) {
    console.log('⚠️ Index: User exists but forcing login display - this should trigger redirect');
  }

  console.log('📝 Index: Rendering login form');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center space-y-4">
            <img 
              src="/lovable-uploads/35701fcb-1aa5-457a-8587-658598a5d940.png" 
              alt="SENSE Logo"
              className="h-12 mx-auto"
            />
            <div>
              <CardTitle className="text-2xl">Admin Login</CardTitle>
              <p className="text-muted-foreground mt-2">
                Sign in to manage demo brands
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSigningIn}
              >
                {isSigningIn ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Need an account?{" "}
                <Link to="/signup" className="text-primary hover:underline">
                  Sign up here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
